import { ApolloProvider } from "@apollo/client";
import { PortalProvider } from "@gorhom/portal";
import { DatabaseProvider } from "@nozbe/watermelondb/react";
import {
  DarkTheme,
  DefaultTheme,
  NavigationContainer,
} from "@react-navigation/native";
import {
  ThemeContext,
  ThemeContextType,
} from "@towersystems/roam-common-native/lib/utilities/theme";
import { registerRootComponent } from "expo";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { SafeAreaProvider } from "react-native-safe-area-context";
import useCachedResources from "../hooks/useCachedResources";
import { client } from "./apollo";
import { App } from "./components/App/App";
import { LocalSettingsController } from "./components/LocalSettingsController/LocalSettingsController";
import { MultipleTabGuard } from "./components/MultipleTabGuard";
import { register } from "./serviceWorkerRegistration";
import { database } from "./utilities/data/database";
import { ThemeProvider } from "@towersystems/roam-common-native";
import React from "react";
import { AppPasswordGuard } from "./components/AppPasswordGuard/AppPasswordGuard";
import { lightColors } from "@towersystems/roam-common/lib/tokens";
import "./utilities/i18n/client";
import { linking } from "./linking";

const MyTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: lightColors.primary,
  },
};

register({});

function Boot() {
  const isLoadingComplete = useCachedResources();

  if (!isLoadingComplete) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <AppPasswordGuard>
        <LocalSettingsController>
          <ThemeProvider
            options={{
              themeScheme: {
                light: {
                  surface: "#f5f5f5",
                  cardBackgroundColor: "rgba(255,255,255,0.2)",
                },
                dark: {},
              },
            }}
          >
            <ThemeContext.Consumer
              children={(theme) => (
                <MultipleTabGuard>
                  <DatabaseProvider database={database}>
                    <NavigationContainer
                      linking={linking}
                      theme={
                        (theme as ThemeContextType).scheme === "dark"
                          ? DarkTheme
                          : MyTheme
                      }
                      onStateChange={() => {
                        if ("serviceWorker" in navigator) {
                          navigator.serviceWorker.ready.then((registration) => {
                            if (registration.waiting) {
                              {
                                // send the skip message to kick off the service worker install.
                              }
                              registration.waiting.postMessage({
                                type: "SKIP_WAITING",
                              });
                              {
                                // add an listener to reload page when the new service worker is ready.
                              }
                              registration.waiting.addEventListener(
                                "statechange",
                                (event: Event) => {
                                  const { state = "" } =
                                    (event.target as unknown as {
                                      state: string;
                                    }) || {};
                                  if (state === "activated") {
                                    window.location.reload();
                                  }
                                },
                              );
                            }
                          });
                        }
                      }}
                    >
                      <SafeAreaProvider>
                        <ApolloProvider client={client}>
                          <PortalProvider>
                            <App />
                          </PortalProvider>
                        </ApolloProvider>
                      </SafeAreaProvider>
                    </NavigationContainer>
                  </DatabaseProvider>
                </MultipleTabGuard>
              )}
            />
          </ThemeProvider>
        </LocalSettingsController>
      </AppPasswordGuard>
    </GestureHandlerRootView>
  );
}

export default registerRootComponent(Boot);
